import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../auth/AuthProvider';
import { COLORS, FONTS } from '../../styles/tokens';
import { fetchClients, createClient, createRealmRole } from '../../api/keycloak-admin';
import { uploadAppIcon } from '../../lib/deploy-api';
import type { KeycloakClient } from '../../api/keycloak-admin';
import { filterAppClients, loadUserCounts, slugify, normalizeAppUrl, isKeycloakRestartError } from './app-management-helpers';
import { AddAppForm } from './AddAppForm';
import { AppTable } from '../../components/admin/AppTable';
import { fetchDeployedAppsBySlug, type DeployedAppSummary } from '../../components/admin/AppStatusBadge';
import { useToast } from '../../contexts/ToastContext';

/** App management page - clickable list of apps */
export function AppManagement(): React.JSX.Element {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const navigate = useNavigate();
  const toast = useToast();

  const [clients, setClients] = useState<KeycloakClient[]>([]);
  const [userCounts, setUserCounts] = useState<Record<string, number>>({});
  const [deployedBySlug, setDeployedBySlug] = useState<Record<string, DeployedAppSummary>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [keycloakRestarting, setKeycloakRestarting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  /**
   * Primary load — fetches clients, deploy state, and user counts. Flips the
   * loading spinner only on first render; the background poller calls
   * `refreshQuiet` instead so the UI doesn't flash every few seconds.
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [all, deployed] = await Promise.all([
        fetchClients(token),
        fetchDeployedAppsBySlug(token),
      ]);
      const filtered = filterAppClients(all);
      setClients(filtered);
      setDeployedBySlug(deployed);
      setKeycloakRestarting(false);
      setError(null);
      await loadUserCounts(token, filtered, setUserCounts);
    } catch (err) {
      if (isKeycloakRestartError(err)) {
        /* Transient — keep the existing table and show a soft banner instead. */
        setKeycloakRestarting(true);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  /** Silent background refresh — just the deploy-state snapshot, no spinner, no user-count refetch. */
  const refreshQuiet = useCallback(async () => {
    try {
      const deployed = await fetchDeployedAppsBySlug(token);
      setDeployedBySlug(deployed);
    } catch { /* ignore transient errors during background refresh */ }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  /**
   * Poll deploy state every 10s only while at least one row is in-progress.
   * Polling stops once every row reaches a terminal state (running, failed,
   * stopped). Uses `refreshQuiet` so there's no loading flash.
   */
  useEffect(() => {
    const hasInProgress = Object.values(deployedBySlug).some(d => {
      /* A build that already failed leaves `deploy_status='pending'` as a
         zombie — ignore those so we don't poll forever. */
      if (d.build_status === 'build_failed') return false;
      if (d.deploy_status === 'deploy_failed') return false;
      return d.build_status === 'building' || d.deploy_status === 'pending';
    });
    if (!hasInProgress) return;
    const handle = setInterval(() => { void refreshQuiet(); }, 10_000);
    return () => clearInterval(handle);
  }, [deployedBySlug, refreshQuiet]);

  const handleAddApp = async (name: string, url: string, description: string, team: string, category: string, icon: File | null) => {
    try {
      setError(null);
      const clientId = slugify(name);
      const normalizedUrl = normalizeAppUrl(url);
      if (!normalizedUrl) {
        setError('Enter a valid URL, for example https://myapp.example.com');
        return;
      }
      let iconUrl = '';
      if (icon) {
        iconUrl = await uploadAppIcon(token, clientId, icon);
      }
      await createClient(token, {
        clientId, name, description,
        publicClient: true, enabled: true,
        rootUrl: normalizedUrl, baseUrl: normalizedUrl,
        redirectUris: [`${normalizedUrl}/*`],
        webOrigins: [normalizedUrl],
        attributes: {
          'vertex.team': team,
          'vertex.category': category,
          'vertex.status': 'online',
          'vertex.icon': iconUrl,
        },
      });
      await createRealmRole(token, `app:${clientId}`);
      setShowAddForm(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const blueBtnStyle: React.CSSProperties = {
    height: '34px', padding: '0 16px', borderRadius: '6px', border: 'none',
    background: isDark ? 'linear-gradient(90deg, #0266C6, #00407D)' : 'linear-gradient(90deg, #01A9ED, #0165C6)',
    color: '#FFF', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em',
    textTransform: 'uppercase', cursor: 'pointer',
  };

  return (
    <div>
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
        <h2 className="vx-track" style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: 0 }}>
          App Management
        </h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => navigate('/apps/deploy')} style={blueBtnStyle}>
            Deploy App
          </button>
          <button onClick={() => navigate('/apps/deployed')} style={{
            ...blueBtnStyle,
            background: isDark ? 'linear-gradient(90deg, #1a4a2e, #0f3320)' : 'linear-gradient(90deg, #34d399, #059669)',
          }}>
            Deployed Apps
          </button>
          <button onClick={() => setShowAddForm(!showAddForm)} style={{
            ...blueBtnStyle,
            background: 'transparent',
            border: `1px solid ${colors.border}`,
            color: colors.textSecondary,
          }}>
            {showAddForm ? 'Cancel' : 'Add App'}
          </button>
        </div>
      </div>

      {keycloakRestarting && (
        <div style={{ padding: '10px 14px', backgroundColor: 'rgba(1,169,237,0.08)', border: '1px solid rgba(1,169,237,0.3)', borderRadius: '8px', color: '#60A5FA', fontFamily: FONTS.body, fontSize: '12px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#60A5FA', animation: 'appStatusPulse 1.6s ease-in-out infinite' }} />
          Keycloak is restarting to load the new theme. The app list will refresh automatically.
        </div>
      )}

      {error && !keycloakRestarting && (
        <div style={{ padding: '12px 16px', backgroundColor: isDark ? '#3b1c1c' : '#fde8e8', border: '1px solid #EF4444', borderRadius: '8px', color: '#EF4444', fontFamily: FONTS.body, fontSize: '13px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {showAddForm && <AddAppForm onSubmit={handleAddApp} isDark={isDark} colors={colors} />}

      {loading ? (
        <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>Loading apps...</p>
      ) : (
        <div className="vx-table-wrap" style={{ borderRadius: '10px', border: `1px solid ${colors.border}`, overflowX: 'auto' }}>
          <AppTable
            clients={clients} userCounts={userCounts} deployedBySlug={deployedBySlug}
            isDark={isDark} colors={colors}
            onRowClick={(id) => navigate(`/apps/${id}`)}
            onCopied={message => toast.push({ variant: 'success', title: message, timeoutMs: 2500 })}
          />
        </div>
      )}
    </div>
  );
}
